import { ChevronDown, Info, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { TARGET_FIELDS } from './fieldMappingConfig';

interface Props {
  headers: string[];
  sampleRows: Record<string, string>[];
  mappings: Record<string, string>;  // source col → target field
  onChange: (mappings: Record<string, string>) => void;
}

function ValidationBadge({ isMapped, isRequired }: { isMapped: boolean; isRequired: boolean }) {
  if (isMapped && isRequired) return (
    <span style={{
      display:'inline-flex', alignItems:'center', gap:4,
      background:'rgba(52,211,153,0.12)', border:'1px solid rgba(52,211,153,0.25)',
      borderRadius:6, padding:'2px 8px', fontSize:11.5, color:'#34d399',
    }}>
      <CheckCircle2 size={10} /> Required · mapped
    </span>
  );
  if (!isMapped && isRequired) return (
    <span style={{
      display:'inline-flex', alignItems:'center', gap:4,
      background:'rgba(251,191,36,0.10)', border:'1px solid rgba(251,191,36,0.30)',
      borderRadius:6, padding:'2px 8px', fontSize:11.5, color:'#fbbf24',
    }}>
      <AlertTriangle size={10} /> Required
    </span>
  );
  if (isMapped) return (
    <span style={{
      display:'inline-flex', alignItems:'center', gap:4,
      background:'rgba(96,165,250,0.10)', border:'1px solid rgba(96,165,250,0.20)',
      borderRadius:6, padding:'2px 8px', fontSize:11.5, color:'#60a5fa',
    }}>
      <CheckCircle2 size={10} /> Mapped
    </span>
  );
  return (
    <span style={{
      background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.10)',
      borderRadius:6, padding:'2px 8px', fontSize:11.5, color:'rgba(255,255,255,0.30)',
    }}>
      Optional
    </span>
  );
}

export function FieldMappingStep({ headers, sampleRows, mappings, onChange }: Props) {
  const hasRequired = Object.values(mappings).includes('mobileNumber');

  function setMapping(sourceCol: string, targetField: string) {
    const next: Record<string, string> = { ...mappings };
    // Remove any existing mapping to this target (one-to-one)
    Object.keys(next).forEach(k => { if (next[k] === targetField) delete next[k]; });
    if (targetField === '') {
      delete next[sourceCol];
    } else {
      next[sourceCol] = targetField;
    }
    onChange(next);
  }

  function sampleValue(col: string): string {
    return sampleRows[0]?.[col] ?? '';
  }

  // Which source column maps to mobileNumber?
  const mobileCol = Object.keys(mappings).find(k => mappings[k] === 'mobileNumber');

  return (
    <div className="flex flex-col gap-4">
      {/* Status bar */}
      {!hasRequired && (
        <div style={{
          display:'flex', alignItems:'center', gap:10,
          background:'rgba(251,191,36,0.08)', border:'1px solid rgba(251,191,36,0.25)',
          borderRadius:10, padding:'10px 14px',
          color:'#fbbf24', fontSize:13.5,
        }}>
          <AlertTriangle size={15} style={{ flexShrink:0 }} />
          Map at least one source column to <strong style={{margin:'0 4px'}}>Mobile Number</strong> to continue.
        </div>
      )}

      {/* Column table */}
      <div style={{
        background:'rgba(255,255,255,0.02)',
        border:'1px solid rgba(96,165,250,0.12)',
        borderRadius:12, overflow:'hidden',
      }}>
        {/* Header row */}
        <div style={{
          display:'grid', gridTemplateColumns:'1fr 1fr 1fr',
          background:'rgba(255,255,255,0.04)',
          borderBottom:'1px solid rgba(96,165,250,0.10)',
          padding:'10px 16px',
          fontSize:12, fontWeight:600, color:'rgba(255,255,255,0.45)',
          letterSpacing:'0.05em', textTransform:'uppercase',
        }}>
          <span>Source column</span>
          <span>Sample value</span>
          <span>Map to Authmatech field</span>
        </div>

        {headers.map((col, idx) => {
          const target   = mappings[col] ?? '';
          const sample   = sampleValue(col);
          const tfMeta   = TARGET_FIELDS.find(f => f.value === target);
          const isReq    = tfMeta?.required ?? false;
          const isMapped = !!target;
          const isLast   = idx === headers.length - 1;

          return (
            <div
              key={col}
              style={{
                display:'grid', gridTemplateColumns:'1fr 1fr 1fr',
                alignItems:'center',
                padding:'12px 16px',
                borderBottom: isLast ? 'none' : '1px solid rgba(96,165,250,0.07)',
                background: isMapped ? 'rgba(59,130,246,0.03)' : 'transparent',
                transition:'background 0.15s',
              }}
            >
              {/* Source col */}
              <div style={{ fontSize:13.5, color:'rgba(255,255,255,0.75)', fontWeight:500 }}>
                {col}
                {tfMeta && <ValidationBadge isMapped={isMapped} isRequired={isReq} />}
              </div>

              {/* Sample */}
              <div style={{
                fontSize:12.5, color:'rgba(255,255,255,0.38)',
                fontFamily:'monospace', paddingRight:12,
                overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
              }}>
                {sample || <em style={{ opacity:0.4 }}>—</em>}
              </div>

              {/* Target select */}
              <div style={{ position:'relative' }}>
                <select
                  value={target}
                  onChange={e => setMapping(col, e.target.value)}
                  style={{
                    width:'100%', height:36,
                    background:'rgba(255,255,255,0.04)',
                    border:`1.5px solid ${isMapped ? 'rgba(96,165,250,0.30)' : 'rgba(96,165,250,0.12)'}`,
                    borderRadius:8, color:'rgba(255,255,255,0.80)', fontSize:13.5,
                    padding:'0 32px 0 10px',
                    appearance:'none', cursor:'pointer',
                    outline:'none', fontFamily:'inherit',
                    transition:'border-color 0.15s',
                  }}
                >
                  <option value="" style={{ background:'#0b1629', color:'rgba(255,255,255,0.45)' }}>
                    — skip column —
                  </option>
                  {TARGET_FIELDS.map(f => (
                    <option
                      key={f.value} value={f.value}
                      style={{ background:'#0b1629', color:'rgba(255,255,255,0.80)' }}
                      disabled={f.value !== target && Object.values(mappings).includes(f.value)}
                    >
                      {f.label}{f.required ? ' *' : ''}
                    </option>
                  ))}
                </select>
                <ChevronDown size={13} style={{
                  position:'absolute', right:9, top:'50%',
                  transform:'translateY(-50%)',
                  color:'rgba(255,255,255,0.35)', pointerEvents:'none',
                }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{
        display:'flex', flexWrap:'wrap', gap:16,
        fontSize:12, color:'rgba(255,255,255,0.35)',
        alignItems:'center',
      }}>
        <span style={{ display:'flex', alignItems:'center', gap:4 }}>
          <Info size={11} /> <span>* Required field</span>
        </span>
        <span>· Mobile Number is the unique key used to match records.</span>
        {mobileCol && (
          <span style={{ color:'#34d399' }}>
            ✓ Using column "<strong>{mobileCol}</strong>" as Mobile Number.
          </span>
        )}
      </div>
    </div>
  );
}
